/**
 * DataTable Component
 * Tabela única das listagens do sistema: cabeçalho, linhas, estado vazio,
 * carregando, coluna de ações, paginação em pt-BR e fallback em cards no mobile.
 *
 * Existe para que nenhuma listagem monte `<Table>` na mão — foi assim que o
 * sistema acumulou 24 tabelas locais divergentes (10 com paginação, 14 sem).
 * O ESLint bloqueia `Table*` do MUI dentro de `src/pages/**`.
 *
 * @example
 * const columns: DataTableColumn<Company>[] = [
 *   { id: 'name', label: 'Empresa', primary: true, sortable: true, render: (c) => c.tradeName ?? c.legalName },
 *   { id: 'cnpj', label: 'CNPJ', render: (c) => formatCNPJ(c.cnpj) },
 *   { id: 'status', label: 'Status', render: (c) => <Chip size="small" label={c.status} /> },
 * ];
 *
 * <DataTable
 *   columns={columns}
 *   rows={companies}
 *   getRowId={(c) => c.id}
 *   loading={isLoading}
 *   emptyState={{
 *     title: 'Nenhuma empresa no seu acesso',
 *     description: 'Empresas são a base dos funcionários, dos exames e dos agendamentos.',
 *   }}
 *   actions={[{ icon: <EditIcon fontSize="small" />, tooltip: 'Editar', onClick: onEdit }]}
 *   sort={{ by: sortBy, order: sortOrder, onChange: handleSort }}
 *   pagination={{ page, rowsPerPage, totalCount, onPageChange, onRowsPerPageChange }}
 * />
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  alpha,
  type Theme,
} from '@mui/material';

import { EmptyState, EmptyStateConfig } from './EmptyState';

export interface DataTableColumn<T> {
  /** Identificador único da coluna. */
  id: string;
  /** Rótulo do cabeçalho. */
  label: string;
  /** Conteúdo da célula. */
  render: (row: T) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  width?: number | string;
  /** Título do card no mobile. Marque exatamente uma coluna como `primary`. */
  primary?: boolean;
  /** Oculta a coluna no card do mobile. */
  hideOnMobile?: boolean;
  /** Cabeçalho vira controle de ordenação. Exige a prop `sort` na tabela. */
  sortable?: boolean;
  /**
   * Corta o conteúdo na largura declarada em vez de deixar a coluna crescer com
   * o texto. Exige `width` — é de lá que sai a largura respeitada. As
   * reticências continuam por conta do conteúdo (`textOverflow: 'ellipsis'`).
   */
  truncate?: boolean;
}

export type DataTableSortOrder = 'asc' | 'desc';

export interface DataTableSort {
  /** `id` da coluna ordenada agora. */
  by: string;
  order: DataTableSortOrder;
  /**
   * A tabela só emite o pedido — quem ordena é a API. Ordenar em memória
   * mentiria em toda listagem paginada: reordenaria a página, não a lista.
   */
  onChange: (by: string, order: DataTableSortOrder) => void;
}

export interface DataTableSelection {
  selected: (string | number)[];
  onChange: (selected: (string | number)[]) => void;
  /** Ações em lote. A barra só aparece quando há linha marcada. */
  actions?: React.ReactNode;
}

export interface DataTableAction<T> {
  icon: React.ReactNode;
  tooltip: string;
  onClick: (row: T) => void;
  color?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  /** Esconde a ação nesta linha (permissão, estado do registro). */
  hidden?: (row: T) => boolean;
  /**
   * Deixa a ação à vista, sem efeito. É o formato que o acesso usa: bloqueio explicado vai no
   * `tooltip`, porque esconder o ícone esconde junto o que a pessoa precisa saber para
   * destravar. Ver `useControle`, em `authorization/useVeredito`.
   *
   * Aceita valor solto porque a resposta de acesso é da tela inteira, e não da linha: exigir
   * `() => x` de quinze chamadas só para casar com a assinatura de `hidden` seria ruído.
   */
  disabled?: boolean | ((row: T) => boolean);
}

export interface DataTablePagination {
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string | number;
  loading?: boolean;
  /** Estado vazio da listagem. Obrigatório: o vazio explica de onde vêm os dados. */
  emptyState: EmptyStateConfig;
  /** Há filtro aplicado: o vazio fala de filtro, não de cadastro que falta. */
  filtersActive?: boolean;
  onClearFilters?: () => void;
  actions?: DataTableAction<T>[];
  onRowClick?: (row: T) => void;
  /** Ordenação pedida à API. Omita para listagem sem ordenação. */
  sort?: DataTableSort;
  /** Seleção múltipla com ações em lote. Omita para listagem sem seleção. */
  selection?: DataTableSelection;
  /** Omita para listagem sem paginação. */
  pagination?: DataTablePagination;
  /**
   * A tabela dentro de um cartão que já a emoldura: sem o `Paper` próprio, para não ficar
   * cartão dentro de cartão. Fora dele, omita.
   */
  embedded?: boolean;
  /**
   * Linhas em destaque de sucesso (borda e fundo verdes), como a que acabou de ser gravada.
   * A cor sozinha não basta: quem usa precisa dizer o mesmo em texto dentro da linha.
   */
  highlightRow?: (row: T) => boolean;
  /**
   * Tom de uma linha que não é sucesso: `info` (borda azul, a que acabou de passar a ser
   * editável aqui) ou `muted` (fundo leve, a que vem de outro lugar e não se edita aqui). Como
   * no destaque, a cor não basta: a linha diz o mesmo em texto.
   */
  rowTone?: (row: T) => 'info' | 'muted' | null;
}

/** Fundo verde claro que ainda deixa o texto verde-escuro com contraste acima de 4,5:1. */
const highlightSx = (theme: Theme) => ({
  bgcolor: alpha(theme.palette.success.main, 0.06),
  outline: `2px solid ${theme.palette.success.main}`,
  outlineOffset: '-2px',
  '&.MuiTableRow-hover:hover': { bgcolor: alpha(theme.palette.success.main, 0.06) },
});

const infoSx = (theme: Theme) => ({
  outline: `2px solid ${theme.palette.info.main}`,
  outlineOffset: '-2px',
});

const mutedSx = (theme: Theme) => ({
  bgcolor: theme.palette.action.hover,
});

/**
 * `table-layout: auto` (o padrão) trata `width` de coluna como sugestão: com
 * texto que não quebra, a célula cresce até caber tudo e o `overflow: hidden`
 * de dentro nunca dispara. `max-width: 0` é o que faz o navegador respeitar a
 * largura declarada — medido no Chromium, a coluna de 30% da listagem de
 * empresas ia a 1526px com um nome de 120 caracteres, e com ele fica em 300px.
 */
const TRUNCATED_CELL_SX = { maxWidth: 0, overflow: 'hidden' } as const;

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];
const SKELETON_ROWS = 5;

export function DataTable<T>({
  columns,
  rows,
  getRowId,
  loading = false,
  emptyState,
  filtersActive,
  onClearFilters,
  actions,
  onRowClick,
  sort,
  selection,
  pagination,
  embedded = false,
  highlightRow,
  rowTone,
}: DataTableProps<T>): React.ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const hasActions = Boolean(actions && actions.length > 0);
  const hasSelection = Boolean(selection);
  const colSpan = columns.length + (hasActions ? 1 : 0) + (hasSelection ? 1 : 0);

  const rowIds = rows.map(getRowId);
  const selectedIds = new Set(selection?.selected ?? []);
  const allSelected = rows.length > 0 && rowIds.every((id) => selectedIds.has(id));
  const someSelected = !allSelected && rowIds.some((id) => selectedIds.has(id));
  const selectedCount = selection?.selected.length ?? 0;

  const toggleRow = (id: string | number): void => {
    if (!selection) return;
    selection.onChange(
      selectedIds.has(id)
        ? selection.selected.filter((selected) => selected !== id)
        : [...selection.selected, id],
    );
  };

  const toggleAllRows = (): void => {
    if (!selection) return;
    selection.onChange(allSelected ? [] : rowIds);
  };

  const requestSort = (columnId: string): void => {
    if (!sort) return;
    const nextOrder: DataTableSortOrder =
      sort.by === columnId && sort.order === 'asc' ? 'desc' : 'asc';
    sort.onChange(columnId, nextOrder);
  };

  const renderHeaderLabel = (column: DataTableColumn<T>): React.ReactNode => {
    if (!sort || !column.sortable) return column.label;

    return (
      <TableSortLabel
        active={sort.by === column.id}
        direction={sort.by === column.id ? sort.order : 'asc'}
        onClick={() => requestSort(column.id)}
      >
        {column.label}
      </TableSortLabel>
    );
  };

  const renderRowCheckbox = (row: T): React.ReactNode => (
    <Checkbox
      size="small"
      checked={selectedIds.has(getRowId(row))}
      onChange={() => toggleRow(getRowId(row))}
      inputProps={{ 'aria-label': 'Selecionar esta linha' }}
    />
  );

  // A barra de ações em lote só existe quando há o que aplicar: sem seleção
  // ela ocuparia espaço permanente para dizer "0 selecionados".
  const selectionBar = selection && selectedCount > 0 && (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1,
        bgcolor: 'action.selected',
      }}
    >
      <Typography variant="body2" fontWeight="medium">
        {selectedCount} selecionado{selectedCount > 1 ? 's' : ''}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>{selection.actions}</Box>
    </Box>
  );

  const visibleActions = (row: T): DataTableAction<T>[] =>
    (actions ?? []).filter((action) => !action.hidden?.(row));

  const actionDisabled = (action: DataTableAction<T>, row: T): boolean =>
    typeof action.disabled === 'function' ? action.disabled(row) : (action.disabled ?? false);

  const renderActions = (row: T): React.ReactNode => (
    <Box display="flex" gap={0.5} justifyContent="center">
      {visibleActions(row).map((action) => {
        const desabilitada = actionDisabled(action, row);
        const botao = (
          <IconButton
            size="small"
            aria-label={action.tooltip}
            color={action.color ?? 'default'}
            disabled={desabilitada}
            onClick={(event) => {
              event.stopPropagation();
              action.onClick(row);
            }}
          >
            {action.icon}
          </IconButton>
        );

        // Botão desabilitado não emite evento de mouse, e sem o `<span>` o tooltip — que aqui é
        // a única explicação que a pessoa recebe — nunca abriria.
        return (
          <Tooltip key={action.tooltip} title={action.tooltip}>
            {desabilitada ? <span>{botao}</span> : botao}
          </Tooltip>
        );
      })}
    </Box>
  );

  const paginationNode = pagination && (
    <TablePagination
      component="div"
      count={pagination.totalCount}
      page={pagination.page}
      onPageChange={(_, newPage) => pagination.onPageChange(newPage)}
      rowsPerPage={pagination.rowsPerPage}
      onRowsPerPageChange={(event) =>
        pagination.onRowsPerPageChange(parseInt(event.target.value, 10))
      }
      rowsPerPageOptions={pagination.rowsPerPageOptions ?? DEFAULT_ROWS_PER_PAGE_OPTIONS}
      labelRowsPerPage="Itens por página:"
      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
    />
  );

  // ---------- Mobile: cards ----------
  if (isMobile) {
    const primaryColumn = columns.find((column) => column.primary) ?? columns[0];
    const detailColumns = columns.filter(
      (column) => column !== primaryColumn && !column.hideOnMobile,
    );
    // Dentro de um cartão (embedded), as linhas não ganham moldura própria: um traço as separa.
    const moldura = embedded ? 'div' : Card;
    const molduraSx = (indice: number) =>
      embedded ? (indice > 0 ? { borderTop: 1, borderColor: 'divider' } : {}) : { mb: 2 };

    return (
      <Box>
        {selectionBar}

        {loading ? (
          Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <Box component={moldura} key={index} sx={molduraSx(index)}>
              <CardContent>
                <Skeleton width="60%" height={28} />
                <Skeleton width="40%" />
              </CardContent>
            </Box>
          ))
        ) : rows.length === 0 ? (
          <Box component={moldura} sx={molduraSx(0)}>
            <CardContent>
              <EmptyState
                config={emptyState}
                filtersActive={filtersActive}
                onClearFilters={onClearFilters}
                compact
              />
            </CardContent>
          </Box>
        ) : (
          rows.map((row, indice) => (
            <Box
              component={moldura}
              key={getRowId(row)}
              data-destaque={highlightRow?.(row) || undefined}
              sx={[
                molduraSx(indice),
                { cursor: onRowClick ? 'pointer' : 'default' },
                rowTone?.(row) === 'muted' && mutedSx,
                rowTone?.(row) === 'info' && infoSx,
                Boolean(highlightRow?.(row)) && highlightSx,
              ]}
              onClick={() => onRowClick?.(row)}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1,
                    mb: detailColumns.length > 0 ? 2 : 0,
                  }}
                >
                  {hasSelection && (
                    <Box onClick={(event) => event.stopPropagation()}>
                      {renderRowCheckbox(row)}
                    </Box>
                  )}
                  <Typography variant="subtitle1" fontWeight="bold">
                    {primaryColumn.render(row)}
                  </Typography>
                  {hasActions && renderActions(row)}
                </Box>

                <Stack spacing={1}>
                  {detailColumns.map((column) => (
                    <Box
                      key={column.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {column.label}:
                      </Typography>
                      <Box sx={{ textAlign: 'right' }}>{column.render(row)}</Box>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Box>
          ))
        )}

        {paginationNode}
      </Box>
    );
  }

  // ---------- Desktop: tabela ----------
  // Tabela e paginação dividem o mesmo Paper: a paginação faz parte da listagem,
  // não é um bloco solto abaixo dela.
  const listagem = (
    <>
      {selectionBar}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {hasSelection && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAllRows}
                    inputProps={{ 'aria-label': 'Selecionar todas as linhas' }}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align ?? 'left'}
                  sortDirection={sort && sort.by === column.id ? sort.order : false}
                  sx={column.width ? { width: column.width } : undefined}
                >
                  {renderHeaderLabel(column)}
                </TableCell>
              ))}
              {hasActions && <TableCell align="center">Ações</TableCell>}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {Array.from({ length: colSpan }, (_, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 0 }}>
                  <EmptyState
                    config={emptyState}
                    filtersActive={filtersActive}
                    onClearFilters={onClearFilters}
                  />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={getRowId(row)}
                  hover
                  data-destaque={highlightRow?.(row) || undefined}
                  sx={[
                    { cursor: onRowClick ? 'pointer' : 'default' },
                    rowTone?.(row) === 'muted' && mutedSx,
                    rowTone?.(row) === 'info' && infoSx,
                    Boolean(highlightRow?.(row)) && highlightSx,
                  ]}
                  onClick={() => onRowClick?.(row)}
                  selected={selectedIds.has(getRowId(row))}
                >
                  {hasSelection && (
                    <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                      {renderRowCheckbox(row)}
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align ?? 'left'}
                      sx={column.truncate ? TRUNCATED_CELL_SX : undefined}
                    >
                      {column.render(row)}
                    </TableCell>
                  ))}
                  {hasActions && (
                    <TableCell align="center" onClick={(event) => event.stopPropagation()}>
                      {renderActions(row)}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {paginationNode}
    </>
  );
  return embedded ? <Box>{listagem}</Box> : <Paper sx={{ mb: 2 }}>{listagem}</Paper>;
}

export default DataTable;
